import { FileItem } from '@inno-sdks/files'
import { create } from 'zustand'

type PrintItem = {
  id: number
  file: FileItem
  processedFile?: Blob
  progress: number
  label: string
  abortController: AbortController
  failedReason?: string
  status: 'pending' | 'completed' | 'failed'
  onCancel: () => void
}

export type PrintStoreState = {
  items: Array<PrintItem>
}

export type PrintStoreActions = {
  addItem(item: PrintItem): void
  updateProgress(id: number, progress: number): void
  markCompleted(id: number, blob?: Blob): void
  markFailed(id: number, failedReason: string): void
  cancelPrint(id: number): void
  clearAll(): void
}

export const usePrintStore = create<PrintStoreState & PrintStoreActions>(
  set => ({
    items: [],
    addItem: item =>
      set(({ items }) => ({
        items: [...items, { ...item, status: 'pending' }]
      })),
    updateProgress: (id, progress) =>
      set(({ items }) => ({
        items: items.map(item =>
          item.id === id ? { ...item, progress } : item
        )
      })),
    markCompleted: (id, blob) =>
      set(({ items }) => ({
        items: items.map(item =>
          item.id === id
            ? {
                ...item,
                status: 'completed',
                processedFile: blob
              }
            : item
        )
      })),
    markFailed: (id, failedReason) =>
      set(({ items }) => ({
        items: items.map(item =>
          item.id === id ? { ...item, status: 'failed', failedReason } : item
        )
      })),
    cancelPrint: id =>
      set(({ items }) => {
        const activity = items.find(item => item.id === id)
        activity?.abortController.abort()
        return {
          items: items.filter(item => item.id !== id)
        }
      }),
    clearAll: () =>
      set(({ items }) => {
        items.forEach(item => {
          if (item.status === 'pending' && item.abortController) {
            item.abortController.abort()
          }
        })
        return { items: [] }
      })
  })
)
